import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  RigidSquareTitle,
  RigidSquareTitleL,
  RigidSquareTitleM,
  RigidSquareTitleS,
} from 'components/landing/ui/RigidSquareTitle';

import bgCardsAvif from './assets/bg-cards.avif';
import bgCards from './assets/bg-cards.jpg';
import bgJerseysAvif from './assets/bg-jerseys.avif';
import bgJerseys from './assets/bg-jerseys.jpg';
import bgMbappeAvif from './assets/bg-mbappe.avif';
import bgMbappe from './assets/bg-mbappe.jpg';
import bgTicketsAvif from './assets/bg-tickets.avif';
import bgTickets from './assets/bg-tickets.jpg';
import cardsAvif from './assets/cards.avif';
import cards from './assets/cards.png';
import jerseysAvif from './assets/jerseys.avif';
import jerseys from './assets/jerseys.png';
import mbappeAvif from './assets/mbappe.avif';
import mbappe from './assets/mbappe.png';
import ticketsAvif from './assets/tickets.avif';
import tickets from './assets/tickets.png';

const Root = styled(Vertical)`
  display: flex;
  flex-direction: column;
  gap: calc(8 * var(--unit));
  align-items: center;
  padding: var(--double-unit);
  width: 100%;
  background: linear-gradient(
    0deg,
    var(--c-neutral-100) 10%,
    #012161 55%,
    var(--c-neutral-100) 100%
  );
  @media ${desktopAndAbove} {
    padding: 0 calc(6 * var(--unit));
  }
`;

const Grid = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: calc(7 * var(--unit));
  max-width: 1360px;
  margin: auto;

  > :nth-child(1) {
    grid-area: mbappe;
    background-image: image-set(
      url(${bgMbappeAvif}) type('image/avif'),
      url(${bgMbappe})
    );
    img {
      position: static;
      transform: scale(1.05);
      min-width: 100%;
      height: 100%;
      object-fit: cover;
      transform-origin: center bottom;
    }
  }
  > :nth-child(2) {
    grid-area: jerseys;
    background-image: image-set(
      url(${bgJerseysAvif}) type('image/avif'),
      url(${bgJerseys})
    );
    img {
      transform: translateY(-20%);
    }
  }
  > :nth-child(3) {
    grid-area: tickets;
    background-image: image-set(
      url(${bgTicketsAvif}) type('image/avif'),
      url(${bgTickets})
    );
    img {
      transform: translateY(-15%);
    }
  }
  > :nth-child(4) {
    grid-area: cards;
    background-image: image-set(
      url(${bgCardsAvif}) type('image/avif'),
      url(${bgCards})
    );
    img {
      transform: translateY(-15%);
    }
  }
  @media ${desktopAndAbove} {
    display: grid;
    gap: calc(6 * var(--unit));
    grid-template:
      'mbappe jerseys jerseys' 314px 'mbappe tickets cards' 350px / minmax(
        624px,
        1fr
      )
      1fr 1fr;
    > :nth-child(1) {
      min-height: 0;
      img {
        position: absolute;
        transform: scale(1.2);
        transform-origin: right bottom;
        height: auto;
      }
    }
  }
`;
const Block = styled.article`
  position: relative;
  background-size: cover;
  border-radius: 20px;
  min-height: 260px;
  img {
    position: absolute;
    top: 0;
    right: 0;
    max-width: 80%;
    @media ${desktopAndAbove} {
      max-width: 100%;
    }
  }
`;
const BlockContent = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: var(--double-unit);
`;
export const Win = () => {
  return (
    <Root>
      <RigidSquareTitle>
        <FormattedMessage
          id="kylianmbappe.Win.title"
          defaultMessage="Win amazing{br}prizes"
          values={{ br: <br /> }}
        />
      </RigidSquareTitle>
      <Grid>
        <Block>
          <picture>
            <source srcSet={mbappeAvif} type="image/avif" />
            <Img
              src={mbappe}
              alt=""
              width="697"
              height="780"
              fetchpriority="low"
            />
          </picture>
          <BlockContent>
            <RigidSquareTitleL>
              <FormattedMessage
                id="kylianmbappe.Win.mbappe"
                defaultMessage="Meet Kylian Mbappé"
              />
            </RigidSquareTitleL>
          </BlockContent>
        </Block>
        <Block>
          <picture>
            <source srcSet={jerseysAvif} type="image/avif" />
            <Img
              src={jerseys}
              alt=""
              width="407"
              height="332"
              fetchpriority="low"
            />
          </picture>
          <BlockContent>
            <RigidSquareTitleM>
              <FormattedMessage
                id="kylianmbappe.Win.jerseys"
                defaultMessage="signed{br}jerseys"
                values={{ br: <br /> }}
              />
            </RigidSquareTitleM>
          </BlockContent>
        </Block>
        <Block>
          <picture>
            <source srcSet={ticketsAvif} type="image/avif" />
            <Img
              src={tickets}
              alt=""
              width="282"
              height="288"
              fetchpriority="low"
            />
          </picture>
          <BlockContent>
            <RigidSquareTitleS>
              <FormattedMessage
                id="kylianmbappe.Win.tickets"
                defaultMessage="Vip{br}Tickets"
                values={{ br: <br /> }}
              />
            </RigidSquareTitleS>
          </BlockContent>
        </Block>
        <Block>
          <picture>
            <source srcSet={cardsAvif} type="image/avif" />
            <Img
              src={cards}
              alt=""
              width="314"
              height="296"
              fetchpriority="low"
            />
          </picture>
          <BlockContent>
            <RigidSquareTitleS>
              <FormattedMessage
                id="kylianmbappe.Win.cards"
                defaultMessage="Sorare{br}cards"
                values={{ br: <br /> }}
              />
            </RigidSquareTitleS>
          </BlockContent>
        </Block>
      </Grid>
    </Root>
  );
};
